import React, { useState, useCallback, useEffect, memo } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Checkbox,
  ListItemText,
  Stack,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";
import {
  FilterContainer,
  FilterButton,
  FilterCount,
  FilterPopoverContent,
  FilterHeader,
  FilterOptions,
  ClearAllButton,
  SearchAndFilterContainer,
} from "../Table/TableStyles";
import DatePicker from "../DatePicker/DatePicker";
import CloseIcon from "@mui/icons-material/Close";

const Filter = ({
  filters,
  columnFilters,
  onFilterChange,
  isStatic = false,
}) => {
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterValues, setFilterValues] = useState(filters || {});
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    setFilterValues(filters);
  }, [filters]);

  const handleFilterValueChange = useCallback(
    (field, value, isMultiple = true) => {
      setFilterValues((prev) => {
        const currentValues = prev[field] || [];
        let newValues;

        if (isMultiple) {
          newValues = currentValues.includes(value)
            ? currentValues.filter((v) => v !== value)
            : [...currentValues, value].filter(Boolean);
        } else {
          newValues = value;
        }

        const updatedValues = {
          ...prev,
          [field]: newValues,
        };

        if (
          field === "dateRange" &&
          Array.isArray(value) &&
          value[0] &&
          value[1]
        ) {
          updatedValues.startDate = dayjs(value[0]).format("YYYY-MM-DD");
          updatedValues.endDate = dayjs(value[1]).format("YYYY-MM-DD");
        }

        const valueToSend =
          Array.isArray(newValues) && newValues.length === 0 ? null : newValues;
        onFilterChange(field, valueToSend);
        return updatedValues;
      });
    },
    [onFilterChange]
  );

  const handleClearFilter = useCallback(
    (e, field) => {
      e.stopPropagation();
      const filter = columnFilters.find((f) => f.field === field);

      const emptyValue = filter?.type === "daterange" ? [null, null] : [];

      setFilterValues((prev) => ({
        ...prev,
        [field]: emptyValue,
        ...(field === "dateRange" && {
          startDate: null,
          endDate: null,
        }),
      }));

      onFilterChange(field, emptyValue);
    },
    [columnFilters, onFilterChange]
  );

  const handleClearAll = useCallback(() => {
    const clearedValues = columnFilters.reduce((acc, filter) => {
      acc[filter.field] = filter.type === "daterange" ? [null, null] : [];
      return acc;
    }, {});

    if (filterValues.hasOwnProperty("dateRange")) {
      clearedValues.startDate = null;
      clearedValues.endDate = null;
    }

    setFilterValues(clearedValues);

    Object.keys(clearedValues).forEach((field) => {
      onFilterChange(field, clearedValues[field]);
    });

    setActiveFilter(null);
  }, [columnFilters, filterValues, onFilterChange]);

  const hasFilterValue = useCallback(
    (field) => {
      const value = filterValues[field];
      if (!value) return false;

      if (Array.isArray(value)) {
        if (field === "dateRange") {
          return value[0] && value[1];
        }
        return value.length > 0;
      }
      return false;
    },
    [filterValues]
  );

  const hasAnyActiveFilters = useCallback(() => {
    return columnFilters.some((filter) => hasFilterValue(filter.field));
  }, [columnFilters, hasFilterValue]);

  const getFilterDisplayValue = useCallback(
    (filter) => {
      const value = filterValues[filter.field];

      if (
        filter.type === "daterange" &&
        Array.isArray(value) &&
        value.length === 2
      ) {
        if (value[0] && value[1]) return "1";
        return "";
      }

      if (filter.type === "select" && Array.isArray(value)) {
        return value.length > 0 ? value.length.toString() : "";
      }

      return "";
    },
    [filterValues]
  );

  const renderFilterContent = useCallback(
    (filter) => {
      if (filter.type === "select") {
        return (
          <FilterPopoverContent>
            <FilterHeader>
              <Typography variant="subtitle2">
                Filter by {filter.label}
              </Typography>
              <IconButton size="small" onClick={() => setActiveFilter(null)}>
                <CloseIcon />
              </IconButton>
            </FilterHeader>
            <FilterOptions>
              {filter.options?.map((option) => (
                <MenuItem
                  key={option.value}
                  dense
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFilterValueChange(
                      filter.field,
                      option.value,
                      filter.multiple !== false
                    );
                  }}
                >
                  <Checkbox
                    checked={filterValues[filter.field]?.includes(option.value)}
                    onChange={() => {}}
                    size="small"
                  />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      style: { fontSize: "0.875rem" },
                    }}
                  />
                </MenuItem>
              ))}
            </FilterOptions>
          </FilterPopoverContent>
        );
      }

      if (filter.type === "daterange") {
        return (
          <DatePicker
            value={filterValues[filter.field] || [null, null]}
            onChange={(value) => {
              handleFilterValueChange(filter.field, value, false);
              if (value?.[0] && value?.[1]) {
                setActiveFilter(null);
                setAnchorEl(null);
              }
            }}
            onClose={() => {}}
          />
        );
      }
    },
    [filterValues, handleFilterValueChange]
  );

  return (
    <FilterContainer>
      {columnFilters.map((filter) => {
        const hasActiveFilters = hasFilterValue(filter.field);
        const isDateRange = filter.type === "daterange";

        return (
          <React.Fragment key={filter.field}>
            <Box sx={{ position: "relative" }}>
              <FilterButton
                active={activeFilter === filter.field}
                hasFilters={hasActiveFilters}
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                  setActiveFilter(
                    activeFilter === filter.field ? null : filter.field
                  );
                }}
                aria-expanded={activeFilter === filter.field}
                aria-haspopup="true"
                data-filter-id={filter.field}
              >
                {filter.icon && (
                  <span className="filter-icon">{filter.icon}</span>
                )}
                <span>{filter.label}</span>
                {hasActiveFilters && (
                  <>
                    <FilterCount>{getFilterDisplayValue(filter)}</FilterCount>
                    <CloseIcon
                      className="clear-icon"
                      onClick={(e) => handleClearFilter(e, filter.field)}
                    />
                  </>
                )}
                <KeyboardArrowDownIcon className="send-icon" />
              </FilterButton>

              {isDateRange && activeFilter === filter.field && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    zIndex: 2000,
                    bgcolor: "background.paper",
                    borderRadius: "8px",
                  }}
                  ref={(node) => {
                    if (node) {
                      const rect = node.getBoundingClientRect();
                      const viewportWidth = window.innerWidth;

                      if (rect.right > viewportWidth - 16) {
                        node.style.left = "auto";
                        node.style.right = "0";
                        const arrow = node.querySelector("&:before");
                        if (arrow) {
                          arrow.style.left = "auto";
                          arrow.style.right = "20px";
                        }
                      }

                      if (rect.left < 16) {
                        node.style.left = "0";
                        node.style.right = "auto";
                      }
                    }
                  }}
                >
                  {renderFilterContent(filter)}
                </Box>
              )}
            </Box>
          </React.Fragment>
        );
      })}

      <Menu
        anchorEl={anchorEl}
        open={
          Boolean(activeFilter) &&
          columnFilters.find((f) => f.field === activeFilter)?.type !==
            "daterange"
        }
        onClose={() => {
          setActiveFilter(null);
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            mt: 1,
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              left: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        {activeFilter &&
          columnFilters.find((f) => f.field === activeFilter)?.type !==
            "daterange" &&
          renderFilterContent(
            columnFilters.find((f) => f.field === activeFilter)
          )}
      </Menu>

      {hasAnyActiveFilters() && (
        <ClearAllButton
          variant="outlined"
          onClick={handleClearAll}
          startIcon={<CloseIcon />}
        >
          Clear All
        </ClearAllButton>
      )}
    </FilterContainer>
  );
};

export default Filter;
